import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth as authAPI } from '../services/api';
import Avatar from '../components/Avatar';
import '../styles/Settings.css';

const Settings = () => {
  const { user, updateUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    email: '',
    bio: '',
    city: '',
    birthDate: '',
    isPublic: true,
    password: '',
    confirmPassword: ''
  });

  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        nickname: user.nickname || '',
        email: user.email || '',
        bio: user.bio || '',
        city: user.city || '',
        birthDate: user.birthDate ? user.birthDate.split('T')[0] : '',
        isPublic: user.isPublic !== undefined ? user.isPublic : true,
        password: '',
        confirmPassword: ''
      });
      setImagePreview(user.profileImage || '');
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('A kép mérete maximum 5MB lehet');
        return;
      }

      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Csak JPG, PNG és WebP képek engedélyezettek');
        return;
      }

      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
      setRemoveImage(false);
      setError('');
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview('');
    setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (formData.bio.length > 500) {
      setError('A bio maximum 500 karakter lehet');
      return;
    }

    if (formData.nickname && formData.nickname.length > 30) {
      setError('A becenév maximum 30 karakter lehet');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setError('A jelszónak minimum 6 karakter hosszúnak kell lennie');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('A jelszavak nem egyeznek');
      return;
    }

    setLoading(true);

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('nickname', formData.nickname);
      submitData.append('bio', formData.bio);
      submitData.append('city', formData.city);
      submitData.append('birthDate', formData.birthDate);
      submitData.append('isPublic', formData.isPublic);

      if (formData.password) {
        submitData.append('password', formData.password);
      }

      if (profileImage) {
        submitData.append('image', profileImage);
      } else if (removeImage) {
        submitData.append('removeProfileImage', 'true');
      }

      const response = await authAPI.updateProfile(submitData);

      // Update user context
      updateUser(response.data);

      setSuccess('Profil sikeresen frissítve!');

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      setError(err.response?.data?.message || 'Hiba történt a profil frissítése során');
      console.error('Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="container settings-container">
        <p>Betöltés...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container settings-container">
        <p>Kérlek jelentkezz be a beállítások eléréséhez.</p>
      </div>
    );
  }

  return (
    <div className="container settings-container">
      <div className="settings-header">
        <h1>Beállítások</h1>
        <p>Szerkeszd a profil adataidat</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Profile Image Section */}
        <section className="settings-section">
          <h2>Profilkép</h2>
          <div className="settings-image-upload">
            <div className="settings-image-preview">
              {imagePreview && !removeImage ? (
                <img src={imagePreview} alt="Profile" className="settings-image-current" />
              ) : (
                <Avatar name={formData.name} size="xlarge" />
              )}
            </div>
            <div className="settings-image-controls">
              <label htmlFor="image-upload" className="btn btn-outline">
                Kép feltöltése
              </label>
              <input
                type="file"
                id="image-upload"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              {(imagePreview || user.profileImage) && !removeImage && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="btn btn-outline btn-danger"
                >
                  Kép törlése
                </button>
              )}
              <p className="settings-image-help">
                JPG, PNG vagy WebP, maximum 5MB
              </p>
            </div>
          </div>
        </section>

        {/* Basic Info Section */}
        <section className="settings-section">
          <h2>Alapadatok</h2>
          <div className="form-group">
            <label htmlFor="name">Teljes név *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="nickname">Becenév</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              maxLength="30"
              className="form-control"
              placeholder="Opcionális (max 30 karakter)"
            />
            <small className="form-help">
              {formData.nickname.length}/30 karakter
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email cím *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="bio">Bemutatkozás</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              maxLength="500"
              rows="4"
              className="form-control"
              placeholder="Írj néhány mondatot magadról..."
            />
            <small className="form-help">
              {formData.bio.length}/500 karakter
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="city">Város</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              maxLength="100"
              className="form-control"
              placeholder="Pl. Budapest"
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthDate">Születési dátum</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              className="form-control"
            />
            <small className="form-help">
              Minimum 13 éves kornak kell lenned
            </small>
          </div>
        </section>

        {/* Privacy Section */}
        <section className="settings-section">
          <h2>Adatvédelem</h2>
          <div className="form-group-checkbox">
            <label htmlFor="isPublic" className="checkbox-label">
              <input
                type="checkbox"
                id="isPublic"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleChange}
              />
              <span>Publikus profil</span>
            </label>
            <small className="form-help">
              Ha engedélyezed, mások megtekinthetik a profilodat és a statisztikáidat
            </small>
          </div>
        </section>

        {/* Password Section */}
        <section className="settings-section">
          <h2>Jelszó módosítása</h2>
          <p className="settings-section-description">
            Hagyd üresen, ha nem szeretnéd módosítani
          </p>

          <div className="form-group">
            <label htmlFor="password">Új jelszó</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              minLength="6"
              className="form-control"
              placeholder="Minimum 6 karakter"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Jelszó megerősítése</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="form-control"
              placeholder="Írd be újra az új jelszót"
            />
          </div>
        </section>

        {/* Submit Buttons */}
        <div className="settings-actions">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="btn btn-outline"
            disabled={loading}
          >
            Mégse
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Mentés...' : 'Változtatások mentése'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
